'use client';

import { useAuth } from '@/hooks/useAuth';
import {
  Loader2, PlusCircle, Trash2, Edit, GripVertical, Camera, X, Route, Check,
  BookImage, Church, Users, CalendarDays, Image as ImageIcon, Music2, Clock, AlertTriangle,
  CheckCircle2, CircleDashed, Cross, Eraser, Search, Sparkles, Shirt, Palette, Ruler, BookOpen,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  getDiasSS,
  createDiaSS,
  updateDiaSSHermandad,
  getProgramaSSConfig,
  setProgramaSSPortadaLibro,
  setCarreraOficial,
  setCatedralPunto,
  setDiaPortada,
  removeDiaPortada,
  reviewRecorrido,
  reviewHorario,
  adminClearRecorridos,
  adminClearHorarios,
  adminDeleteSolicitud,
  adminUpdateDiaSS,
  getCityMapConfig,
  syncProgramaSSMapCalib,
  uploadProgramaSSImage,
  renderProgramaSSAhora,
} from '@/lib/actions';
import { DiaSS, JartinetHermandad, Banda, DiaSSHermandadDetalles, HorarioSolicitud, ProgramaSSConfig, RecorridoPunto } from '@/lib/types';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent, useDroppable, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '@/firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { RouteMapEditor, CARRERA_OFICIAL_COLOR, darkenColor } from '@/components/programa-ss/RouteMapEditor';
import { CuadroHorariosEditor, CuadroHorariosTabla, type ItinerarioRecord } from '@/components/programa-ss/CuadroHorariosEditor';
import { escalaDelMapa, formatKm, longitudEnMetros, metrosDelRecorrido, normalizeDia, ordenarDiasSS } from '@/lib/programa-ss';

const moveHermandadBetweenDias = httpsCallable(functions, 'moveHermandadBetweenDias');
const updateDiaSS = httpsCallable(functions, 'updateDiaSS');

// ─── Utilidades ──────────────────────────────────────────────────────────────

function normalizeDetalles(hermandades: DiaSS['hermandades']): DiaSSHermandadDetalles[] {
  if (!Array.isArray(hermandades)) return [];
  return hermandades
    .map((h) => (typeof h === 'string' ? ({ hermandadId: h } as DiaSSHermandadDetalles) : h))
    .filter((h) => h && h.hermandadId);
}

function formatFecha(fecha: any): string {
  if (!fecha) return '';
  const d = typeof fecha?.toDate === 'function' ? fecha.toDate() : new Date(fecha);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
}

/** Fecha en formato `yyyy-MM-dd` para el `<input type="date">`. */
function fechaInput(fecha: any): string {
  if (!fecha) return '';
  const d = typeof fecha?.toDate === 'function' ? fecha.toDate() : new Date(fecha);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Calendario oficial de la Semana Santa de Jartiland 2026: el Miércoles de
 * Pasión cae el 2 de septiembre y el Domingo de Resurrección el 13, así que los
 * doce días son consecutivos desde esa fecha (la Madrugá comparte fecha con el
 * Viernes Santo). La clave es el nombre del día normalizado.
 */
const SS_2026: { nombre: string; fecha: string; tipo: 'visperas' | 'semanasanta'; orden: number }[] = [
  { nombre: 'miercoles de pasion', fecha: '2026-09-02', tipo: 'visperas', orden: 1 },
  { nombre: 'jueves de pasion', fecha: '2026-09-03', tipo: 'visperas', orden: 2 },
  { nombre: 'viernes de dolores', fecha: '2026-09-04', tipo: 'visperas', orden: 3 },
  { nombre: 'sabado de pasion', fecha: '2026-09-05', tipo: 'visperas', orden: 4 },
  { nombre: 'domingo de ramos', fecha: '2026-09-06', tipo: 'semanasanta', orden: 5 },
  { nombre: 'lunes santo', fecha: '2026-09-07', tipo: 'semanasanta', orden: 6 },
  { nombre: 'martes santo', fecha: '2026-09-08', tipo: 'semanasanta', orden: 7 },
  { nombre: 'miercoles santo', fecha: '2026-09-09', tipo: 'semanasanta', orden: 8 },
  { nombre: 'jueves santo', fecha: '2026-09-10', tipo: 'semanasanta', orden: 9 },
  { nombre: 'madruga', fecha: '2026-09-11', tipo: 'semanasanta', orden: 10 },
  { nombre: 'viernes santo', fecha: '2026-09-11', tipo: 'semanasanta', orden: 11 },
  { nombre: 'sabado santo', fecha: '2026-09-12', tipo: 'semanasanta', orden: 12 },
  { nombre: 'domingo de resurreccion', fecha: '2026-09-13', tipo: 'semanasanta', orden: 13 },
];

/**
 * Normaliza el nombre del día para casarlo con el calendario oficial. Usa la
 * misma comparación tolerante que el orden litúrgico (`@/lib/programa-ss`), que
 * además **colapsa letras repetidas**: en el programa hay días escritos como
 * "Domingo de Resurección", con una sola erre, y así también se reconocen.
 */
function normaliza(s: string): string {
  return normalizeDia(s);
}

function fechaOficialDe(nombredia: string) {
  const n = normaliza(nombredia);
  // Los dos lados se normalizan igual (sin espacios ni letras repetidas).
  return SS_2026.find((d) => {
    const oficial = normaliza(d.nombre);
    return oficial === n || n.includes(oficial) || oficial.includes(n);
  });
}

/** Estado del recorrido de una hermandad, con su color y etiqueta. */
const RECORRIDO_ESTADO = {
  aprobado: { label: 'Recorrido aprobado', cls: 'text-emerald-400', Icon: CheckCircle2 },
  pendiente: { label: 'Recorrido pendiente de revisar', cls: 'text-amber-400', Icon: AlertTriangle },
  rechazado: { label: 'Recorrido rechazado', cls: 'text-rose-400', Icon: X },
  ninguno: { label: 'Sin recorrido enviado', cls: 'text-muted-foreground/60', Icon: CircleDashed },
} as const;

function estadoRecorrido(d: DiaSSHermandadDetalles): keyof typeof RECORRIDO_ESTADO {
  return (d.recorrido?.estado as keyof typeof RECORRIDO_ESTADO) || 'ninguno';
}

/** Estado del cuadro de horarios. `manual` = lo escribió el admin, sin solicitud. */
const HORARIO_ESTADO = {
  aprobado: { label: 'Cuadro de horarios aprobado', corto: 'aprobado', cls: 'text-emerald-400', chip: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400', Icon: CheckCircle2 },
  pendiente: { label: 'Cuadro de horarios pendiente de revisar', corto: 'pendiente', cls: 'text-amber-400', chip: 'border-amber-500/30 bg-amber-500/10 text-amber-400', Icon: AlertTriangle },
  rechazado: { label: 'Cuadro de horarios rechazado', corto: 'rechazado', cls: 'text-rose-400', chip: 'border-rose-500/30 bg-rose-500/10 text-rose-400', Icon: X },
  manual: { label: 'Cuadro de horarios puesto por el admin', corto: 'manual', cls: 'text-sky-400', chip: 'border-sky-500/30 bg-sky-500/10 text-sky-300', Icon: Clock },
  ninguno: { label: 'Sin cuadro de horarios', corto: 'sin enviar', cls: 'text-muted-foreground/60', chip: 'border-white/10 bg-white/5 text-muted-foreground', Icon: CircleDashed },
} as const;

function estadoHorario(d: DiaSSHermandadDetalles): keyof typeof HORARIO_ESTADO {
  if (d.horario?.estado) return d.horario.estado;
  return Object.keys(d.itinerario || {}).length > 0 ? 'manual' : 'ninguno';
}

// ─── Formularios ─────────────────────────────────────────────────────────────

function CreateDayForm({ onFormSuccess }: { onFormSuccess: () => void }) {
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    try {
      const result = await createDiaSS(formData);
      if (result.success) {
        toast({ title: 'Éxito', description: 'Día creado correctamente.' });
        formRef.current?.reset();
        onFormSuccess();
      } else {
        toast({ title: 'Error', description: result.message, variant: 'destructive' });
      }
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="nombredia">Nombre del Día</Label>
          <Input id="nombredia" name="nombredia" placeholder="Domingo de Ramos" required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="tipo">Sección</Label>
          <Select name="tipo" required>
            <SelectTrigger><SelectValue placeholder="Elige una sección..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="visperas">Vísperas (sin Carrera Oficial)</SelectItem>
              <SelectItem value="semanasanta">Semana Santa</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="orden">Orden</Label>
          <Input id="orden" name="orden" type="number" required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fecha">Fecha</Label>
          <Input id="fecha" name="fecha" type="date" required />
        </div>
      </div>
      <DialogFooter>
        <DialogClose asChild><Button variant="ghost" type="button">Cancelar</Button></DialogClose>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Crear Día
        </Button>
      </DialogFooter>
    </form>
  );
}

/** Edición de los metadatos de un día ya creado (nombre, sección, orden, fecha). */
function EditDayDialog({ dia, onClose, onSaved }: { dia: DiaSS; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const [nombredia, setNombredia] = useState(dia.nombredia || '');
  const [tipo, setTipo] = useState(dia.tipo || 'semanasanta');
  const [orden, setOrden] = useState(String(dia.orden ?? 0));
  const [fecha, setFecha] = useState(fechaInput(dia.fecha));
  const [saving, setSaving] = useState(false);

  const oficial = fechaOficialDe(nombredia);

  const guardar = async () => {
    setSaving(true);
    const res = await adminUpdateDiaSS(dia.id, { nombredia: nombredia.trim(), tipo, orden: Number(orden) || 0, fecha });
    setSaving(false);
    toast({ title: res.success ? 'Guardado' : 'Error', description: res.message, variant: res.success ? undefined : 'destructive' });
    if (res.success) { onSaved(); onClose(); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar día</DialogTitle>
          <DialogDescription>Nombre, sección, orden y fecha de este día del programa.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Nombre del día</Label>
            <Input value={nombredia} onChange={(e) => setNombredia(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Sección</Label>
              <Select value={tipo} onValueChange={(v) => setTipo(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="visperas">Vísperas</SelectItem>
                  <SelectItem value="semanasanta">Semana Santa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Orden</Label>
              <Input type="number" value={orden} onChange={(e) => setOrden(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Fecha</Label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            {oficial && oficial.fecha !== fecha && (
              <button type="button" onClick={() => { setFecha(oficial.fecha); setTipo(oficial.tipo); setOrden(String(oficial.orden)); }} className="text-xs text-primary underline">
                Usar la fecha oficial de 2026 ({oficial.fecha})
              </button>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDetailsForm({ dia, hermandadId, hermandad, allBandas, programaConfig, onFormSuccess, onRefresh, onClose }: {
  dia: DiaSS;
  hermandadId: string;
  hermandad?: JartinetHermandad;
  allBandas: Banda[];
  programaConfig: ProgramaSSConfig | null;
  onFormSuccess: () => void;
  /** Recarga los datos sin cerrar el diálogo (guardados sueltos, como el color). */
  onRefresh: () => void;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [borrando, setBorrando] = useState<'recorrido' | 'horario' | null>(null);
  const [confirmarBorrado, setConfirmarBorrado] = useState<'recorrido' | 'horario' | null>(null);
  const [subiendo, setSubiendo] = useState<'foto' | 'nazarenoUrl' | 'nazarenoUrl2' | null>(null);
  const fotoInputRef = useRef<HTMLInputElement>(null);
  const nazarenoInputRef = useRef<HTMLInputElement>(null);
  const nazareno2InputRef = useRef<HTMLInputElement>(null);

  const initialDetails = useMemo(() => {
    const existing = normalizeDetalles(dia.hermandades).find((h) => h.hermandadId === hermandadId);
    return existing || ({ pasos: '1 PASO', acompanamientoMusical: ['SIN_BANDA'], numeroDeNazarenos: 0, itinerario: {} } as any);
  }, [dia, hermandadId]);

  const [details, setDetails] = useState<Omit<DiaSSHermandadDetalles, 'hermandadId'>>(initialDetails);
  const horarioSolicitud: HorarioSolicitud | undefined = initialDetails.horario;

  // Color del recorrido ya aprobado: se puede cambiar sin tener que rechazarlo
  // y que la hermandad lo vuelva a mandar (`reviewRecorrido` reescribe el color
  // manteniendo el trazado y el estado en aprobado).
  const recorrido = initialDetails.recorrido as DiaSSHermandadDetalles['recorrido'];
  const [colorGuardado, setColorGuardado] = useState(recorrido?.color || '');
  const [colorRecorrido, setColorRecorrido] = useState(recorrido?.color || hermandad?.colorPrimario || '#3b82f6');
  const [guardandoColor, setGuardandoColor] = useState(false);

  const guardarColor = async () => {
    setGuardandoColor(true);
    const res = await reviewRecorrido(dia.id, hermandadId, 'aprobado', colorRecorrido);
    toast({ title: res.success ? 'Color guardado' : 'Error', description: res.success ? 'El recorrido saldrá con el color nuevo.' : res.message, variant: res.success ? undefined : 'destructive' });
    setGuardandoColor(false);
    if (res.success) { setColorGuardado(colorRecorrido); onRefresh(); }
  };

  // Distancia real del recorrido: 1 bloque de Minecraft = 1 metro.
  const escala = useMemo(() => escalaDelMapa(programaConfig?.mapCalib), [programaConfig?.mapCalib]);
  const metros = useMemo(() => {
    const carrera = dia.tipo === 'visperas' ? 0 : longitudEnMetros(programaConfig?.carreraOficial, escala);
    return metrosDelRecorrido(recorrido, escala, carrera);
  }, [recorrido, escala, programaConfig?.carreraOficial, dia.tipo]);

  /** Sube una imagen del libro (foto de la hermandad o del nazareno). */
  const subirImagen = async (
    e: React.ChangeEvent<HTMLInputElement>,
    campo: 'fotoUrl' | 'nazarenoUrl' | 'nazarenoUrl2',
    ref: React.RefObject<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(campo === 'fotoUrl' ? 'foto' : campo);
    try {
      const url = await uploadProgramaSSImage(file, `dias/${dia.id}/hermandades/${hermandadId}_${campo}_${Date.now()}_${file.name}`);
      handleDetailChange(campo, url);
    } catch (err: any) {
      toast({ title: 'Error', description: `No se pudo subir la imagen: ${err.message}`, variant: 'destructive' });
    }
    setSubiendo(null);
    if (ref.current) ref.current.value = '';
  };

  const handleDetailChange = <K extends keyof typeof details>(key: K, value: (typeof details)[K]) => {
    setDetails((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // `recorrido` y `horario` se dejan fuera del envío a propósito: la CF hace
    // merge, así que las claves omitidas se conservan tal cual están guardadas
    // y no se reescriben con los Timestamps ya convertidos a texto.
    const { recorrido, horario, ...aGuardar } = details as any;
    const result = await updateDiaSSHermandad(dia.id, hermandadId, aGuardar);
    if (result.success) {
      toast({ title: 'Éxito', description: 'Detalles actualizados correctamente.' });
      onFormSuccess();
      onClose();
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };

  const borrarSolicitud = async (tipo: 'recorrido' | 'horario') => {
    setBorrando(tipo);
    const result = await adminDeleteSolicitud(dia.id, hermandadId, tipo);
    toast({ title: result.success ? 'Hecho' : 'Error', description: result.message, variant: result.success ? undefined : 'destructive' });
    setBorrando(null);
    setConfirmarBorrado(null);
    if (result.success) { onFormSuccess(); onClose(); }
  };

  const bandOptions = [<SelectItem key="sin-banda" value="SIN_BANDA">Sin Banda</SelectItem>, ...allBandas.map((b) => <SelectItem key={b.id} value={b.id}>{b.nombreCompleto}</SelectItem>)];
  const acompanamiento = details.acompanamientoMusical || [];

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Div plano con scroll NATIVO (patrón de los diálogos con formulario): el
          `ScrollArea` de shadcn escondía la barra y este formulario es largo. */}
      <div className="min-h-0 flex-1 overflow-y-auto pr-2 [scrollbar-color:hsl(var(--primary))_transparent] [scrollbar-width:thin]">
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Pasos</Label>
              <Select value={details.pasos} onValueChange={(v) => handleDetailChange('pasos', v as '1 PASO' | '2 PASOS')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1 PASO">1 PASO</SelectItem><SelectItem value="2 PASOS">2 PASOS</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="nazarenos">Número de Nazarenos</Label>
              <Input id="nazarenos" type="number" value={details.numeroDeNazarenos} onChange={(e) => handleDetailChange('numeroDeNazarenos', Number(e.target.value))} />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2"><Music2 className="h-4 w-4 text-primary" />Acompañamiento Musical</Label>
            <Select value={acompanamiento[0] || 'SIN_BANDA'} onValueChange={(v) => handleDetailChange('acompanamientoMusical', [v, acompanamiento[1] || 'SIN_BANDA'])}>
              <SelectTrigger><SelectValue placeholder="Banda del primer paso..." /></SelectTrigger>
              <SelectContent>{bandOptions}</SelectContent>
            </Select>
            {details.pasos === '2 PASOS' && (
              <Select value={acompanamiento[1] || 'SIN_BANDA'} onValueChange={(v) => handleDetailChange('acompanamientoMusical', [acompanamiento[0], v])}>
                <SelectTrigger className="mt-2"><SelectValue placeholder="Banda del segundo paso..." /></SelectTrigger>
                <SelectContent>{bandOptions}</SelectContent>
              </Select>
            )}
          </div>

          {/* Imágenes del libro: la foto va a la izquierda de la tarjeta y el
              nazareno a la derecha. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Camera className="h-4 w-4 text-primary" />Foto de la hermandad</Label>
              <div className="flex items-center gap-3">
                <div className="flex h-16 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40">
                  {details.fotoUrl ? <img src={details.fotoUrl} alt="" className="h-full w-full object-cover" /> : <Camera className="h-5 w-5 text-muted-foreground" />}
                </div>
                <div className="flex flex-col gap-1">
                  <Button type="button" size="sm" variant="outline" onClick={() => fotoInputRef.current?.click()} disabled={subiendo === 'foto'}>
                    {subiendo === 'foto' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                    {details.fotoUrl ? 'Cambiar' : 'Subir'}
                  </Button>
                  {details.fotoUrl && (
                    <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => handleDetailChange('fotoUrl', '')}>
                      Quitar
                    </Button>
                  )}
                </div>
                <input ref={fotoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => subirImagen(e, 'fotoUrl', fotoInputRef)} />
              </div>
              <p className="text-[0.65rem] text-muted-foreground">Sale a la izquierda de la tarjeta.</p>
            </div>

            {/* Hasta DOS nazarenos: si se suben los dos, el libro los pinta en el
                mismo hueco, uno escorado a la izquierda y otro a la derecha. */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Shirt className="h-4 w-4 text-primary" />Imágenes del nazareno</Label>
              <div className="flex flex-wrap gap-4">
                {([
                  { campo: 'nazarenoUrl', ref: nazarenoInputRef, titulo: 'Nazareno 1' },
                  { campo: 'nazarenoUrl2', ref: nazareno2InputRef, titulo: 'Nazareno 2 (opcional)' },
                ] as const).map(({ campo, ref, titulo }) => (
                  <div key={campo} className="space-y-1">
                    <p className="text-[0.65rem] font-medium text-muted-foreground">{titulo}</p>
                    <div className="flex items-center gap-2">
                      <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40">
                        {details[campo] ? <img src={details[campo]} alt="" className="h-full w-full object-contain" /> : <Shirt className="h-5 w-5 text-muted-foreground" />}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button type="button" size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={subiendo === campo}>
                          {subiendo === campo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                          {details[campo] ? 'Cambiar' : 'Subir'}
                        </Button>
                        {details[campo] && (
                          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => handleDetailChange(campo, '')}>
                            Quitar
                          </Button>
                        )}
                      </div>
                      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => subirImagen(e, campo, ref)} />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[0.65rem] text-muted-foreground">Salen a la derecha de la tarjeta. Con dos, una se escora a la izquierda y otra a la derecha.</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2"><Shirt className="h-4 w-4 text-primary" />Hábito nazareno</Label>
            <RichTextarea
              value={details.habito || ''}
              onChange={(e) => handleDetailChange('habito', e.target.value)}
              placeholder="Túnica de cola de ruán negro, antifaz de terciopelo morado, cíngulo dorado…"
              rows={3}
            />
            <p className="text-[0.65rem] text-muted-foreground">Sustituye al antiguo apartado «Info» en el libro.</p>
          </div>

          {/* Color del recorrido: se puede retocar aunque ya esté aprobado. */}
          {recorrido && (
            <div className="space-y-2 rounded-2xl border border-white/8 bg-muted/40 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="flex items-center gap-2"><Palette className="h-4 w-4 text-primary" />Color del recorrido</Label>
                <span className={cn('rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold', RECORRIDO_ESTADO[estadoRecorrido(initialDetails)].cls)}>
                  {RECORRIDO_ESTADO[estadoRecorrido(initialDetails)].label}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="color"
                  value={colorRecorrido}
                  onChange={(e) => setColorRecorrido(e.target.value)}
                  className="h-9 w-14 flex-shrink-0 cursor-pointer rounded border"
                />
                <Input
                  value={colorRecorrido}
                  onChange={(e) => setColorRecorrido(e.target.value)}
                  className="h-9 w-28 font-mono text-xs uppercase"
                />
                {hermandad?.colorPrimario && hermandad.colorPrimario.toLowerCase() !== colorRecorrido.toLowerCase() && (
                  <Button type="button" size="sm" variant="ghost" onClick={() => setColorRecorrido(hermandad.colorPrimario!)}>
                    <span className="h-3 w-3 rounded-full border border-white/30" style={{ background: hermandad.colorPrimario }} />
                    Color de la hermandad
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  className="ml-auto"
                  onClick={guardarColor}
                  disabled={guardandoColor || colorRecorrido === colorGuardado}
                >
                  {guardandoColor ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Guardar color
                </Button>
              </div>

              {/* Se aplica al instante, aparte del botón "Guardar cambios". */}
              <p className="text-[0.65rem] text-muted-foreground">
                Se guarda por separado y deja el recorrido <strong>aprobado</strong>.
                {metros !== null && <> El trazado mide <strong className="text-primary">{formatKm(metros)}</strong>{dia.tipo !== 'visperas' && ' (Carrera Oficial incluida)'}.</>}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" />Cuadro de horarios</Label>
              {horarioSolicitud && (
                <span className={cn('rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold', HORARIO_ESTADO[horarioSolicitud.estado].chip)}>
                  Enviado por la hermandad · {HORARIO_ESTADO[horarioSolicitud.estado].corto}
                </span>
              )}
            </div>
            {/* El admin manda siempre: puede retocar el cuadro aunque ya esté aprobado. */}
            <CuadroHorariosEditor
              value={details.itinerario as ItinerarioRecord}
              onChange={(nuevo) => handleDetailChange('itinerario', nuevo)}
              esVispera={dia.tipo === 'visperas'}
            />
          </div>

          {(initialDetails.recorrido || initialDetails.horario || Object.keys(details.itinerario || {}).length > 0) && (
            <div className="space-y-2 rounded-2xl border border-destructive/30 bg-destructive/5 p-3">
              <p className="flex items-center gap-2 text-xs font-semibold text-destructive"><Eraser className="h-3.5 w-3.5" /> Borrar lo que ha enviado esta hermandad</p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" className="text-destructive hover:text-destructive" disabled={!initialDetails.recorrido || !!borrando} onClick={() => setConfirmarBorrado('recorrido')}>
                  <Route className="h-3.5 w-3.5" /> Borrar recorrido
                </Button>
                <Button type="button" size="sm" variant="outline" className="text-destructive hover:text-destructive" disabled={(!initialDetails.horario && !Object.keys(initialDetails.itinerario || {}).length) || !!borrando} onClick={() => setConfirmarBorrado('horario')}>
                  <Clock className="h-3.5 w-3.5" /> Borrar cuadro de horarios
                </Button>
              </div>
              <p className="text-[0.65rem] text-muted-foreground">
                Al borrarlos, la hermandad los podrá volver a enviar desde su pestaña «Recorrido y cuadro de horarios».
              </p>
            </div>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" disabled={isSubmitting}>{isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar Cambios</Button>
      </DialogFooter>

      <AlertDialog open={!!confirmarBorrado} onOpenChange={(o) => !o && setConfirmarBorrado(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmarBorrado === 'recorrido' ? '¿Borrar el recorrido enviado?' : '¿Borrar el cuadro de horarios?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmarBorrado === 'recorrido'
                ? 'Se elimina el trazado de esta hermandad para este día. Tendrá que dibujarlo otra vez.'
                : 'Se eliminan las horas de esta hermandad para este día, incluidas las que hayas retocado tú. Tendrá que enviarlo otra vez.'}
              {' '}Esto no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); if (confirmarBorrado) borrarSolicitud(confirmarBorrado); }} disabled={!!borrando}>
              {borrando && <Loader2 className="h-4 w-4 animate-spin" />} Sí, borrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}

// ─── Fila de hermandad dentro de un día ──────────────────────────────────────

function HermandadRow({ detalle, hermandad, allBandas, onEdit, listeners, attributes, dragging }: {
  detalle: DiaSSHermandadDetalles;
  hermandad?: JartinetHermandad;
  allBandas: Banda[];
  onEdit: () => void;
  listeners?: any;
  attributes?: any;
  dragging?: boolean;
}) {
  const estado = estadoRecorrido(detalle);
  const { Icon, cls, label } = RECORRIDO_ESTADO[estado];
  const horario = HORARIO_ESTADO[estadoHorario(detalle)];
  const bandas = (detalle.acompanamientoMusical || []).filter((b) => b && b !== 'SIN_BANDA');
  const nItinerario = Object.keys(detalle.itinerario || {}).length;
  const color = hermandad?.colorPrimario || '#64748b';

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-xl border border-white/8 bg-background/60 p-2.5 transition-colors hover:border-white/20',
        dragging && 'opacity-30',
      )}
    >
      <span className="absolute inset-y-0 left-0 w-[3px]" style={{ background: color }} />

      <div className="flex items-center gap-2 pl-1.5">
        {listeners && (
          <button type="button" className="cursor-grab text-muted-foreground/50 hover:text-foreground" {...attributes} {...listeners}>
            <GripVertical className="h-4 w-4" />
          </button>
        )}

        {hermandad?.escudoUrl ? (
          <img src={hermandad.escudoUrl} alt="" className="h-7 w-7 flex-shrink-0 object-contain" />
        ) : (
          <div className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full bg-white/5"><Church className="h-3.5 w-3.5 text-muted-foreground" /></div>
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{hermandad?.nombre || 'Hermandad no encontrada'}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[0.65rem] text-muted-foreground">
            <span>{detalle.pasos || '1 PASO'}</span>
            {!!detalle.numeroDeNazarenos && (
              <span className="flex items-center gap-1"><Users className="h-2.5 w-2.5" />{detalle.numeroDeNazarenos}</span>
            )}
            {bandas.length > 0 && (
              <span className="flex min-w-0 items-center gap-1">
                <Music2 className="h-2.5 w-2.5" />
                <span className="truncate">{bandas.map((b) => allBandas.find((x) => x.id === b)?.nombreCompleto || '—').join(' · ')}</span>
              </span>
            )}
            {nItinerario > 0 && <span className="flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{nItinerario}</span>}
            {detalle.fotoUrl && <ImageIcon className="h-2.5 w-2.5 text-emerald-400" />}
          </div>
        </div>

        <span title={label} className={cn('flex-shrink-0', cls)}><Icon className="h-4 w-4" /></span>
        <span title={horario.label} className={cn('flex-shrink-0', horario.cls)}><horario.Icon className="h-4 w-4" /></span>

        <Button size="icon" variant="ghost" className="h-7 w-7 flex-shrink-0" onClick={onEdit}>
          <Edit className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

const SortableHermandadRow = (props: { id: string } & Parameters<typeof HermandadRow>[0]) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}>
      <HermandadRow {...props} listeners={listeners} attributes={attributes} />
    </div>
  );
};

// ─── Tarjeta de un día ───────────────────────────────────────────────────────

const DroppableDayCard = ({ day, allHermandades, allBandas, setEditingHermandad, setEditingDay, activeId, onPortadaChanged }: {
  day: DiaSS;
  allHermandades: JartinetHermandad[];
  allBandas: Banda[];
  setEditingHermandad: (d: { dia: DiaSS; hermandadId: string }) => void;
  setEditingDay: (d: DiaSS) => void;
  activeId: string | null;
  onPortadaChanged: () => void;
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: day.id });
  const detalles = normalizeDetalles(day.hermandades);
  const hermandadIds = detalles.map((h) => h.hermandadId);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const esVispera = day.tipo === 'visperas';
  // Aviso si la fecha guardada no cuadra con el calendario oficial de 2026.
  const oficial = fechaOficialDe(day.nombredia);
  const fechaCorrecta = !oficial || fechaInput(day.fecha) === oficial.fecha;
  const nazarenos = detalles.reduce((n, d) => n + (d.numeroDeNazarenos || 0), 0);
  const recorridos = detalles.filter((d) => d.recorrido).length;
  const cuadros = detalles.filter((d) => Object.keys(d.itinerario || {}).length > 0).length;
  const pendientes = detalles.filter((d) => d.recorrido?.estado === 'pendiente').length
    + detalles.filter((d) => d.horario?.estado === 'pendiente').length;

  const handlePortadaFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    const result = await setDiaPortada(day.id, file);
    toast({ title: result.success ? 'Éxito' : 'Error', description: result.message, variant: result.success ? undefined : 'destructive' });
    if (result.success) onPortadaChanged();
    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemovePortada = async () => {
    const result = await removeDiaPortada(day.id);
    if (result.success) onPortadaChanged();
  };

  return (
    <Card ref={setNodeRef} className={cn('flex flex-col overflow-hidden transition-all', isOver && 'border-primary ring-2 ring-primary/60')}>
      {/* Cabecera con portada de fondo */}
      <div className="relative">
        {day.portadaUrl && (
          <>
            <img src={day.portadaUrl} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/70 to-transparent" />
          </>
        )}
        <CardHeader className="relative space-y-2 pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <CardTitle className="truncate text-lg">{day.nombredia}</CardTitle>
              <p className={cn('mt-0.5 truncate text-xs capitalize', fechaCorrecta ? 'text-muted-foreground' : 'text-amber-400')}>
                {formatFecha(day.fecha) || 'Sin fecha'}
                {!fechaCorrecta && ' · revisa la fecha'}
              </p>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1">
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingDay(day)} title="Editar día">
                <Edit className="h-3.5 w-3.5" />
              </Button>
              {day.portadaUrl && (
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive/70 hover:text-destructive" onClick={handleRemovePortada} title="Quitar portada">
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => fileInputRef.current?.click()} disabled={isUploading} title="Portada del día">
                {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
              </Button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePortadaFile} />
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[0.65rem] font-semibold">
              {detalles.length} hermandad{detalles.length === 1 ? '' : 'es'}
            </span>
            {nazarenos > 0 && (
              <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[0.65rem] font-semibold">
                {nazarenos.toLocaleString('es-ES')} nazarenos
              </span>
            )}
            <span className={cn(
              'rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold',
              recorridos === detalles.length && detalles.length > 0
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                : 'border-white/10 bg-white/5 text-muted-foreground',
            )}>
              {recorridos}/{detalles.length} recorridos
            </span>
            <span className={cn(
              'rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold',
              cuadros === detalles.length && detalles.length > 0
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                : 'border-white/10 bg-white/5 text-muted-foreground',
            )}>
              {cuadros}/{detalles.length} horarios
            </span>
            {pendientes > 0 && (
              <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[0.65rem] font-semibold text-amber-400">
                {pendientes} por revisar
              </span>
            )}
            {esVispera && (
              <span className="flex items-center gap-1 rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-[0.65rem] font-semibold text-violet-300">
                <Cross className="h-2.5 w-2.5" /> Sin Carrera Oficial
              </span>
            )}
          </div>
        </CardHeader>
      </div>

      <CardContent className="flex-grow space-y-2">
        <SortableContext items={hermandadIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {detalles.map((detalle) => {
              const hermandad = allHermandades.find((h) => h.id === detalle.hermandadId);
              if (!hermandad) return null;
              return (
                <SortableHermandadRow
                  key={hermandad.id}
                  id={hermandad.id}
                  detalle={detalle}
                  hermandad={hermandad}
                  allBandas={allBandas}
                  onEdit={() => setEditingHermandad({ dia: day, hermandadId: hermandad.id })}
                  dragging={activeId === hermandad.id}
                />
              );
            })}
          </div>
        </SortableContext>
        {hermandadIds.length === 0 && (
          <div className="rounded-xl border-2 border-dashed border-white/10 py-8 text-center">
            <p className="text-xs text-muted-foreground">Arrastra una hermandad aquí</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Configuración del libro ─────────────────────────────────────────────────

function ConfigTile({ icon, title, value, done, children }: { icon: React.ReactNode; title: string; value: string; done: boolean; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 rounded-2xl border border-white/8 bg-background/40 p-4">
      <div className="flex items-center gap-2">
        <div className={cn('grid h-9 w-9 place-items-center rounded-xl border', done ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' : 'border-white/10 bg-white/5 text-muted-foreground')}>
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold">{title}</p>
          <p className="truncate text-xs text-muted-foreground">{value}</p>
        </div>
      </div>
      <div className="mt-auto">{children}</div>
    </div>
  );
}

function LibroConfigCard({ config, cityMapUrl, onRefresh }: { config: ProgramaSSConfig | null; cityMapUrl: string | null; onRefresh: () => void }) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCarreraDialogOpen, setIsCarreraDialogOpen] = useState(false);
  const [draftPuntos, setDraftPuntos] = useState<RecorridoPunto[]>(config?.carreraOficial || []);
  const [isSavingCarrera, setIsSavingCarrera] = useState(false);
  const [isCatedralDialogOpen, setIsCatedralDialogOpen] = useState(false);
  const [draftCatedral, setDraftCatedral] = useState<RecorridoPunto | null>(config?.catedralPunto || null);
  const [isSavingCatedral, setIsSavingCatedral] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);

  const handlePortadaLibroFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    const result = await setProgramaSSPortadaLibro(file);
    toast({ title: result.success ? 'Éxito' : 'Error', description: result.message, variant: result.success ? undefined : 'destructive' });
    if (result.success) onRefresh();
    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSaveCarrera = async () => {
    setIsSavingCarrera(true);
    const result = await setCarreraOficial(draftPuntos);
    if (result.success) {
      toast({ title: 'Éxito', description: result.message });
      onRefresh();
      setIsCarreraDialogOpen(false);
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    setIsSavingCarrera(false);
  };

  const handleSaveCatedral = async () => {
    if (!draftCatedral) return;
    setIsSavingCatedral(true);
    const result = await setCatedralPunto(draftCatedral);
    if (result.success) {
      toast({ title: 'Éxito', description: result.message });
      onRefresh();
      setIsCatedralDialogOpen(false);
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    setIsSavingCatedral(false);
  };

  const carreraLista = !!config?.carreraOficial && config.carreraOficial.length >= 2;

  // Escala del mapa: sin ella no se puede dar la distancia de los recorridos.
  const escala = escalaDelMapa(config?.mapCalib);
  const metrosCarrera = longitudEnMetros(config?.carreraOficial, escala);

  const sincronizarEscala = async () => {
    setSincronizando(true);
    const res = await syncProgramaSSMapCalib();
    toast({ title: res.success ? 'Escala actualizada' : 'Error', description: res.message, variant: res.success ? undefined : 'destructive' });
    setSincronizando(false);
    if (res.success) onRefresh();
  };

  // Estado del librito del mod (lo escribe la Cloud Function del render).
  const [render, setRender] = useState<{ hash?: string; count?: number; generatedAt?: number; dirty?: boolean; lastError?: string | null } | null>(null);
  const [renderizando, setRenderizando] = useState(false);

  useEffect(() => {
    const u = onSnapshot(doc(db, 'programa_ss_render', 'main'), (snap) => setRender(snap.exists() ? (snap.data() as any) : null));
    return () => u();
  }, []);

  const regenerarLibrito = async () => {
    setRenderizando(true);
    const res = await renderProgramaSSAhora();
    toast({ title: res.success ? 'Librito regenerado' : 'Error', description: res.message, variant: res.success ? undefined : 'destructive' });
    setRenderizando(false);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg"><BookImage className="h-5 w-5 text-primary" /> Configuración del libro y del mapa</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <ConfigTile
          icon={<BookImage className="h-4 w-4" />}
          title="Portada del librito"
          value={config?.portadaLibroUrl ? 'Subida' : 'Sin portada'}
          done={!!config?.portadaLibroUrl}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-16 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40">
              {config?.portadaLibroUrl ? <img src={config.portadaLibroUrl} alt="" className="h-full w-full object-cover" /> : <BookImage className="h-5 w-5 text-muted-foreground" />}
            </div>
            <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
              {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
              {config?.portadaLibroUrl ? 'Cambiar' : 'Subir'}
            </Button>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePortadaLibroFile} />
          </div>
        </ConfigTile>

        <ConfigTile
          icon={<Route className="h-4 w-4" />}
          title="Carrera Oficial"
          value={carreraLista ? `Definida (${config!.carreraOficial!.length} puntos)` : 'Todavía sin definir'}
          done={carreraLista}
        >
          <Button size="sm" variant="outline" onClick={() => { setDraftPuntos(config?.carreraOficial || []); setIsCarreraDialogOpen(true); }} disabled={!cityMapUrl}>
            <Route className="h-3.5 w-3.5" />
            {carreraLista ? 'Editar' : 'Dibujar'}
          </Button>
          {!cityMapUrl && <p className="mt-1 text-xs text-destructive/80">Falta el mapa de la ciudad.</p>}
          <p className="mt-1 text-[0.65rem] text-muted-foreground">Solo se aplica a los días de Semana Santa; las vísperas no la usan.</p>
        </ConfigTile>

        <ConfigTile
          icon={<Church className="h-4 w-4" />}
          title="Punto de la Catedral"
          value={config?.catedralPunto ? 'Definido' : 'Todavía sin definir'}
          done={!!config?.catedralPunto}
        >
          <Button size="sm" variant="outline" onClick={() => { setDraftCatedral(config?.catedralPunto || null); setIsCatedralDialogOpen(true); }} disabled={!cityMapUrl}>
            <Church className="h-3.5 w-3.5" />
            {config?.catedralPunto ? 'Editar' : 'Marcar'}
          </Button>
        </ConfigTile>

        {/* El mapa del programa es el del servidor, y de este conocemos la
            coordenada de Minecraft de cada píxel: con eso se mide en metros lo
            que anda cada cofradía (1 bloque = 1 metro). La calibración vive en
            `mod_map_config`, que es solo-admin, así que se copia aquí porque el
            libro se ve también sin sesión. */}
        <ConfigTile
          icon={<Ruler className="h-4 w-4" />}
          title="Escala del mapa"
          value={escala ? `${escala.mPorPctX.toFixed(1)} × ${escala.mPorPctY.toFixed(1)} m por 1 %` : 'Sin calibrar'}
          done={!!escala}
        >
          <Button size="sm" variant="outline" onClick={sincronizarEscala} disabled={sincronizando}>
            {sincronizando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ruler className="h-3.5 w-3.5" />}
            Actualizar escala
          </Button>
          <p className="mt-1 text-[0.65rem] text-muted-foreground">
            {escala
              ? <>Se copia sola al abrir esta página. Carrera Oficial: <strong className="text-primary">{metrosCarrera ? formatKm(metrosCarrera) : '—'}</strong>.</>
              : 'Calibra el mapa en «Mapa del servidor» y vuelve aquí: sin escala no se puede dar la distancia de los recorridos.'}
          </p>
        </ConfigTile>

        {/* El librito de dentro de Minecraft son ESTAS carillas fotografiadas
            por Chrome headless: por eso se ve igual que aquí. Se regenera solo
            cuando cambias el programa (repaso cada 5 min); el botón es para no
            esperar. */}
        <ConfigTile
          icon={<BookOpen className="h-4 w-4" />}
          title="Librito del mod"
          value={render?.count ? `${render.count} carillas${render.dirty ? ' · con cambios pendientes' : ''}` : 'Sin generar'}
          done={!!render?.count && !render?.dirty}
        >
          <Button size="sm" variant="outline" onClick={regenerarLibrito} disabled={renderizando}>
            {renderizando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BookOpen className="h-3.5 w-3.5" />}
            Regenerar ahora
          </Button>
          <p className="mt-1 text-[0.65rem] text-muted-foreground">
            {render?.generatedAt
              ? <>Generado {new Date(render.generatedAt).toLocaleString('es-ES')}. {render.dirty ? 'Se regenerará solo en unos minutos.' : 'Al día.'}</>
              : 'Todavía no se ha generado ninguna vez.'}
          </p>
          {render?.lastError && <p className="mt-1 text-[0.65rem] text-destructive/80">Último error: {render.lastError}</p>}
        </ConfigTile>
      </CardContent>

      <Dialog open={isCarreraDialogOpen} onOpenChange={setIsCarreraDialogOpen}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Carrera Oficial</DialogTitle>
            <DialogDescription>
              Haz clic sobre el mapa para ir colocando los puntos. Los hermanos mayores tendrán que enganchar su recorrido a los dos extremos de esta línea.
            </DialogDescription>
          </DialogHeader>
          {cityMapUrl && (
            <div className="space-y-3">
              <RouteMapEditor
                mapImageUrl={cityMapUrl}
                onMapClick={(p) => setDraftPuntos((prev) => [...prev, p])}
                segments={[{ puntos: draftPuntos, color: CARRERA_OFICIAL_COLOR }]}
                pointHandles
                onSegmentPointClick={(_, i) => setDraftPuntos((prev) => prev.filter((_, j) => j !== i))}
                className="h-[55vh]"
              />
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setDraftPuntos((p) => p.slice(0, -1))} disabled={draftPuntos.length === 0}>Deshacer último punto</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setDraftPuntos([])} disabled={draftPuntos.length === 0}>Limpiar</Button>
                </div>
                <p className="text-xs text-muted-foreground">{draftPuntos.length} puntos · clic en un punto para borrarlo</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsCarreraDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveCarrera} disabled={isSavingCarrera || draftPuntos.length < 2}>
              {isSavingCarrera && <Loader2 className="h-4 w-4 animate-spin" />} Guardar Carrera Oficial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isCatedralDialogOpen} onOpenChange={setIsCatedralDialogOpen}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Punto de la Catedral</DialogTitle>
            <DialogDescription>Haz clic sobre el mapa para marcar la ubicación de la Catedral.</DialogDescription>
          </DialogHeader>
          {cityMapUrl && (
            <div className="space-y-3">
              <RouteMapEditor mapImageUrl={cityMapUrl} onMapClick={(p) => setDraftCatedral(p)} catedral={draftCatedral ? { point: draftCatedral } : null} className="h-[55vh]" />
              <div className="flex items-center justify-between">
                <Button type="button" variant="outline" size="sm" onClick={() => setDraftCatedral(null)} disabled={!draftCatedral}>Quitar punto</Button>
                <p className="text-xs text-muted-foreground">Haz clic de nuevo para mover el punto.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsCatedralDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveCatedral} disabled={isSavingCatedral || !draftCatedral}>
              {isSavingCatedral && <Loader2 className="h-4 w-4 animate-spin" />} Guardar Punto de la Catedral
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Revisión de recorridos ──────────────────────────────────────────────────

interface PendingRecorrido {
  dia: DiaSS;
  hermandadId: string;
  detalle: DiaSSHermandadDetalles;
}

function PendingRecorridoCard({ item, allHermandades, cityMapUrl, carreraOficial, catedralPunto, onReviewed }: {
  item: PendingRecorrido;
  allHermandades: JartinetHermandad[];
  cityMapUrl: string | null;
  carreraOficial: RecorridoPunto[] | undefined;
  catedralPunto: RecorridoPunto | undefined;
  onReviewed: () => void;
}) {
  const { toast } = useToast();
  const hermandad = allHermandades.find((h) => h.id === item.hermandadId);
  const [color, setColor] = useState(hermandad?.colorPrimario || '#3b82f6');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const esVispera = item.dia.tipo === 'visperas';
  const mostrarCarrera = !esVispera && !!carreraOficial && carreraOficial.length >= 2;

  const handleReview = async (accion: 'aprobado' | 'rechazado') => {
    setIsSubmitting(true);
    const result = await reviewRecorrido(item.dia.id, item.hermandadId, accion, accion === 'aprobado' ? color : undefined);
    toast({ title: result.success ? 'Éxito' : 'Error', description: result.message, variant: result.success ? undefined : 'destructive' });
    if (result.success) onReviewed();
    setIsSubmitting(false);
  };

  return (
    <Card className="overflow-hidden border-amber-500/30">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          {hermandad?.escudoUrl && <img src={hermandad.escudoUrl} alt="" className="h-8 w-8 object-contain" />}
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate text-base">{hermandad?.nombre || 'Hermandad desconocida'}</CardTitle>
            <p className="truncate text-xs text-muted-foreground">
              {item.dia.nombredia}
              {esVispera && ' · vísperas (sin Carrera Oficial)'}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {cityMapUrl && item.detalle.recorrido && (
          <RouteMapEditor
            mapImageUrl={cityMapUrl}
            reference={mostrarCarrera ? { puntos: carreraOficial!, color: CARRERA_OFICIAL_COLOR } : null}
            referenceEndpointMarkers={mostrarCarrera}
            segments={[
              { puntos: item.detalle.recorrido.ida, color },
              { puntos: item.detalle.recorrido.vuelta, color: darkenColor(color), dashed: true },
            ]}
            showSegmentPoints={false}
            markers={[
              { point: item.detalle.recorrido.iglesia, color, ring: true, iconUrl: hermandad?.escudoUrl },
              ...(item.detalle.recorrido.estacion ? [{ point: item.detalle.recorrido.estacion, color: '#e11d48', ring: true, cross: true }] : []),
            ]}
            catedral={!esVispera && catedralPunto ? { point: catedralPunto } : null}
          />
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor={`color-${item.hermandadId}`} className="text-xs">Color:</Label>
            <input id={`color-${item.hermandadId}`} type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-8 w-12 cursor-pointer rounded border" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => handleReview('rechazado')} disabled={isSubmitting}>
              <X className="h-3.5 w-3.5" /> Rechazar
            </Button>
            <Button size="sm" onClick={() => handleReview('aprobado')} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Aprobar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PendingHorarioCard({ item, allHermandades, onReviewed }: {
  item: PendingRecorrido;
  allHermandades: JartinetHermandad[];
  onReviewed: () => void;
}) {
  const { toast } = useToast();
  const hermandad = allHermandades.find((h) => h.id === item.hermandadId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rechazando, setRechazando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [editando, setEditando] = useState(false);
  const [cuadro, setCuadro] = useState<ItinerarioRecord>((item.detalle.itinerario || {}) as ItinerarioRecord);

  const puntos = Object.keys(item.detalle.itinerario || {}).length;

  const handleReview = async (accion: 'aprobado' | 'rechazado') => {
    setIsSubmitting(true);
    // Si el admin ha retocado el cuadro antes de aprobarlo, se guarda primero.
    if (accion === 'aprobado' && editando) {
      const guardado = await updateDiaSSHermandad(item.dia.id, item.hermandadId, { itinerario: cuadro });
      if (!guardado.success) {
        toast({ title: 'Error', description: guardado.message, variant: 'destructive' });
        setIsSubmitting(false);
        return;
      }
    }
    const result = await reviewHorario(item.dia.id, item.hermandadId, accion, accion === 'rechazado' ? motivo : undefined);
    toast({ title: result.success ? 'Éxito' : 'Error', description: result.message, variant: result.success ? undefined : 'destructive' });
    if (result.success) onReviewed();
    setIsSubmitting(false);
  };

  return (
    <Card className="overflow-hidden border-amber-500/30">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          {hermandad?.escudoUrl && <img src={hermandad.escudoUrl} alt="" className="h-8 w-8 object-contain" />}
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate text-base">{hermandad?.nombre || 'Hermandad desconocida'}</CardTitle>
            <p className="truncate text-xs text-muted-foreground">{item.dia.nombredia} · {puntos} punto{puntos === 1 ? '' : 's'}</p>
          </div>
          <span className="flex flex-shrink-0 items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[0.65rem] font-semibold text-amber-400">
            <Clock className="h-3 w-3" /> Horarios
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="max-h-64 overflow-y-auto pr-1">
          {editando ? (
            <CuadroHorariosEditor
              value={item.detalle.itinerario as ItinerarioRecord}
              onChange={(nuevo) => setCuadro(nuevo)}
              esVispera={item.dia.tipo === 'visperas'}
            />
          ) : (
            <CuadroHorariosTabla itinerario={item.detalle.itinerario as ItinerarioRecord} />
          )}
        </div>

        {rechazando && (
          <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo del rechazo (lo verá el hermano mayor)" className="h-9" />
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button size="sm" variant="ghost" onClick={() => setEditando((v) => !v)}>
            <Edit className="h-3.5 w-3.5" /> {editando ? 'Ver tabla' : 'Retocar antes de aprobar'}
          </Button>
          <div className="flex gap-2">
            {rechazando ? (
              <>
                <Button size="sm" variant="ghost" onClick={() => setRechazando(false)} disabled={isSubmitting}>Cancelar</Button>
                <Button size="sm" variant="destructive" onClick={() => handleReview('rechazado')} disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Confirmar rechazo
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setRechazando(true)} disabled={isSubmitting}>
                  <X className="h-3.5 w-3.5" /> Rechazar
                </Button>
                <Button size="sm" onClick={() => handleReview('aprobado')} disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Aprobar
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Tile de estadística ─────────────────────────────────────────────────────

function StatTile({ icon, value, label, color, hint }: { icon: React.ReactNode; value: string | number; label: string; color: string; hint?: string }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-4">
      <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full blur-2xl" style={{ background: `${color}33` }} />
      <div className="relative flex items-center gap-3">
        <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl border" style={{ borderColor: `${color}55`, background: `${color}1a`, color }}>
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-xl font-black leading-none">{value}</p>
          <p className="mt-1 truncate text-[0.7rem] uppercase tracking-wider text-muted-foreground">{label}</p>
        </div>
      </div>
      {hint && <p className="relative mt-2 text-[0.65rem] text-muted-foreground">{hint}</p>}
    </div>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────

export default function AdminProgramaSS26Page() {
  const { userData, loading, jartinetData } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState<DiaSS[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingHermandad, setEditingHermandad] = useState<{ dia: DiaSS; hermandadId: string } | null>(null);
  const [editingDay, setEditingDay] = useState<DiaSS | null>(null);
  const [aplicandoFechas, setAplicandoFechas] = useState(false);
  const { toast } = useToast();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [programaConfig, setProgramaConfig] = useState<ProgramaSSConfig | null>(null);
  const [cityMapUrl, setCityMapUrl] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [confirmClear, setConfirmClear] = useState<'recorridos' | 'horarios' | null>(null);
  const [clearing, setClearing] = useState(false);

  const allHermandades = jartinetData.hermandades || [];
  const allBandas = jartinetData.bandas || [];
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const fetchPageData = useCallback(async () => {
    setIsLoadingData(true);
    const [daysResult, configResult, cityMapResult] = await Promise.all([getDiasSS(), getProgramaSSConfig(), getCityMapConfig()]);
    if (daysResult.error) toast({ title: 'Error', description: `No se pudieron cargar los datos del programa: ${daysResult.error}`, variant: 'destructive' });
    else setDays(daysResult.dias || []);
    setProgramaConfig(configResult.config);
    setCityMapUrl(cityMapResult.config?.mapImageUrl || null);
    setIsLoadingData(false);
  }, [toast]);

  useEffect(() => {
    if (!loading && userData?.role === 'admin') fetchPageData();
    else if (!loading) navigate('/jartinet');
  }, [userData, loading, navigate, fetchPageData]);

  /**
   * La escala del mapa se refresca sola cada vez que un admin abre esta página:
   * si se recalibra el mapa del servidor, el libro se entera sin que nadie tenga
   * que acordarse de pulsar nada. Si falla, no pasa nada (queda la copia vieja).
   */
  useEffect(() => {
    if (loading || userData?.role !== 'admin') return;
    let cancelado = false;
    syncProgramaSSMapCalib()
      .then((res) => {
        if (!cancelado && res.success) setProgramaConfig((prev) => (prev ? { ...prev, mapCalib: (res as any).calib } : prev));
      })
      .catch(() => { /* la escala guardada sigue valiendo */ });
    return () => { cancelado = true; };
  }, [loading, userData?.role]);

  const pendingRecorridos: PendingRecorrido[] = useMemo(() => {
    const result: PendingRecorrido[] = [];
    days.forEach((dia) => {
      normalizeDetalles(dia.hermandades).forEach((detalle) => {
        if (detalle?.recorrido?.estado === 'pendiente') result.push({ dia, hermandadId: detalle.hermandadId, detalle });
      });
    });
    return result;
  }, [days]);

  const pendingHorarios: PendingRecorrido[] = useMemo(() => {
    const result: PendingRecorrido[] = [];
    days.forEach((dia) => {
      normalizeDetalles(dia.hermandades).forEach((detalle) => {
        if (detalle?.horario?.estado === 'pendiente') result.push({ dia, hermandadId: detalle.hermandadId, detalle });
      });
    });
    return result;
  }, [days]);

  const stats = useMemo(() => {
    const detalles = days.flatMap((d) => normalizeDetalles(d.hermandades));
    const conRecorrido = detalles.filter((d) => d.recorrido).length;
    const conItinerario = detalles.filter((d) => Object.keys(d.itinerario || {}).length > 0).length;
    return {
      dias: days.length,
      hermandades: detalles.length,
      nazarenos: detalles.reduce((n, d) => n + (d.numeroDeNazarenos || 0), 0),
      recorridos: conRecorrido,
      aprobados: detalles.filter((d) => d.recorrido?.estado === 'aprobado').length,
      sinRecorrido: detalles.length - conRecorrido,
      fotos: detalles.filter((d) => d.fotoUrl).length,
      itinerarios: conItinerario,
      horariosAprobados: detalles.filter((d) => d.horario?.estado === 'aprobado').length,
      sinItinerario: detalles.length - conItinerario,
    };
  }, [days]);

  // Orden litúrgico por nombre: el `orden` guardado está duplicado en algunos
  // días (el Domingo de Resurrección salía antes de la Madrugá).
  const sortedDays = useMemo(() => ordenarDiasSS(days), [days]);

  // El buscador solo filtra visualmente; el DnD sigue trabajando sobre `days`.
  const matchesBusqueda = useCallback(
    (d: DiaSS) => {
      const q = busqueda.trim().toLowerCase();
      if (!q) return true;
      if (d.nombredia?.toLowerCase().includes(q)) return true;
      return normalizeDetalles(d.hermandades).some((det) =>
        allHermandades.find((h) => h.id === det.hermandadId)?.nombre?.toLowerCase().includes(q),
      );
    },
    [busqueda, allHermandades],
  );

  const visperas = sortedDays.filter((d) => d.tipo === 'visperas' && matchesBusqueda(d));
  const semanaSanta = sortedDays.filter((d) => d.tipo === 'semanasanta' && matchesBusqueda(d));

  const handleFormSuccess = () => { setIsCreateDialogOpen(false); setEditingHermandad(null); fetchPageData(); };

  // Días cuyo nombre casa con el calendario oficial pero tienen otra fecha.
  const diasDesajustados = useMemo(
    () => days.filter((d) => {
      const of = fechaOficialDe(d.nombredia);
      return of && (fechaInput(d.fecha) !== of.fecha || d.tipo !== of.tipo);
    }),
    [days],
  );

  const aplicarFechasOficiales = async () => {
    setAplicandoFechas(true);
    let ok = 0;
    for (const d of diasDesajustados) {
      const of = fechaOficialDe(d.nombredia)!;
      const res = await adminUpdateDiaSS(d.id, { fecha: of.fecha, tipo: of.tipo, orden: of.orden });
      if (res.success) ok++;
    }
    setAplicandoFechas(false);
    toast({
      title: 'Fechas aplicadas',
      description: `${ok} de ${diasDesajustados.length} día(s) actualizados al calendario oficial de 2026.`,
    });
    fetchPageData();
  };

  const handleClear = async () => {
    if (!confirmClear) return;
    setClearing(true);
    const res = confirmClear === 'recorridos' ? await adminClearRecorridos() : await adminClearHorarios();
    toast({ title: res.success ? 'Hecho' : 'Error', description: res.message, variant: res.success ? undefined : 'destructive' });
    setClearing(false);
    setConfirmClear(null);
    if (res.success) fetchPageData();
  };

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const activeContainerId = active.data.current?.sortable.containerId;
    const overContainerId = over.data.current?.sortable?.containerId || (over.id as string);

    const fromDay = days.find((d) => d.id === activeContainerId);
    const toDay = days.find((d) => d.id === overContainerId);
    if (!fromDay) return;

    if (activeContainerId === overContainerId) {
      if (!Array.isArray(fromDay.hermandades)) return;
      const oldIndex = (fromDay.hermandades as DiaSSHermandadDetalles[]).findIndex((h) => h.hermandadId === active.id);
      const newIndex = (fromDay.hermandades as DiaSSHermandadDetalles[]).findIndex((h) => h.hermandadId === over.id);
      if (oldIndex !== newIndex) {
        const updated = arrayMove(fromDay.hermandades as DiaSSHermandadDetalles[], oldIndex, newIndex);
        setDays((prev) => prev.map((d) => (d.id === fromDay.id ? { ...d, hermandades: updated } : d)));
        try {
          await updateDiaSS({ diaId: fromDay.id, updates: { hermandades: updated } });
          toast({ title: 'Éxito', description: 'Orden de hermandades actualizado.' });
        } catch {
          toast({ title: 'Error', description: 'No se pudo guardar el nuevo orden.', variant: 'destructive' });
          fetchPageData();
        }
      }
    } else {
      if (!toDay) return;
      const hermandadId = active.id as string;
      const hermandadDataToMove = (fromDay.hermandades as DiaSSHermandadDetalles[])?.find((h) => h.hermandadId === hermandadId);
      if (!hermandadDataToMove) return;

      setDays((prevDays) => {
        const newDays = [...prevDays];
        const sourceDayIndex = newDays.findIndex((d) => d.id === activeContainerId);
        const destDayIndex = newDays.findIndex((d) => d.id === overContainerId);
        if (sourceDayIndex === -1 || destDayIndex === -1) return prevDays;

        newDays[sourceDayIndex].hermandades = (newDays[sourceDayIndex].hermandades as DiaSSHermandadDetalles[]).filter((h) => h.hermandadId !== hermandadId);
        const destHermandades = [...(newDays[destDayIndex].hermandades as DiaSSHermandadDetalles[])];
        const overItemIndex = destHermandades.findIndex((h) => h.hermandadId === over.id);
        destHermandades.splice(overItemIndex !== -1 ? overItemIndex : destHermandades.length, 0, hermandadDataToMove);
        newDays[destDayIndex].hermandades = destHermandades;
        return newDays;
      });

      try {
        await moveHermandadBetweenDias({ fromDiaId: activeContainerId, toDiaId: overContainerId, hermandadData: hermandadDataToMove });
        toast({ title: 'Éxito', description: 'Hermandad movida correctamente.' });
      } catch (error: any) {
        toast({ title: 'Error', description: `No se pudo mover la hermandad: ${error.message}`, variant: 'destructive' });
        fetchPageData();
      }
    }
  }, [days, fetchPageData, toast]);

  if (loading || isLoadingData) {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando permisos y cargando datos...</p>
      </div>
    );
  }

  const activeHermandad = activeId ? allHermandades.find((h) => h.id === activeId) : null;

  const renderDaySection = (title: string, subtitle: string, daysToRender: DiaSS[], accent: string) => (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <span className="h-8 w-1 rounded-full" style={{ background: accent }} />
        <div>
          <h2 className="text-2xl font-bold">{title}</h2>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <span className="ml-auto rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold">
          {daysToRender.length} día{daysToRender.length === 1 ? '' : 's'}
        </span>
      </div>
      {daysToRender.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-white/10 py-8 text-center text-sm text-muted-foreground">
          {busqueda ? 'Ningún día coincide con la búsqueda.' : 'No hay días definidos para esta sección.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {daysToRender.map((day) => (
            <DroppableDayCard
              key={day.id}
              day={day}
              allHermandades={allHermandades}
              allBandas={allBandas}
              setEditingHermandad={setEditingHermandad}
              setEditingDay={setEditingDay}
              activeId={activeId}
              onPortadaChanged={fetchPageData}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="space-y-8 pb-10">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-primary/15 via-card/60 to-transparent p-7">
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
          <div className="relative flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <div className="grid h-12 w-12 place-items-center rounded-2xl border border-primary/30 bg-primary/10">
                  <CalendarDays className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="font-headline text-3xl font-bold md:text-4xl">Programa Semana Santa 2026</h1>
                  <p className="text-sm text-muted-foreground">Días, hermandades, itinerarios, recorridos y libro oficial.</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar día u hermandad…" className="w-56 pl-9" />
              </div>
              <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogTrigger asChild><Button size="lg"><PlusCircle className="h-5 w-5" /> Crear Día</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Crear Nuevo Día</DialogTitle>
                    <DialogDescription>Configura un nuevo día para el programa de Semana Santa.</DialogDescription>
                  </DialogHeader>
                  <CreateDayForm onFormSuccess={handleFormSuccess} />
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <StatTile icon={<CalendarDays className="h-5 w-5" />} value={stats.dias} label="Días" color="#fbbf24" />
          <StatTile icon={<Church className="h-5 w-5" />} value={stats.hermandades} label="Hermandades" color="#60a5fa" />
          <StatTile icon={<Users className="h-5 w-5" />} value={stats.nazarenos.toLocaleString('es-ES')} label="Nazarenos" color="#a78bfa" />
          <StatTile icon={<Route className="h-5 w-5" />} value={`${stats.aprobados}/${stats.hermandades}`} label="Recorridos ok" color="#34d399" hint={`${stats.sinRecorrido} sin enviar`} />
          <StatTile icon={<Clock className="h-5 w-5" />} value={`${stats.itinerarios}/${stats.hermandades}`} label="Cuadros de horarios" color="#38bdf8" hint={`${stats.horariosAprobados} aprobado(s) · ${stats.sinItinerario} sin poner`} />
          <StatTile icon={<ImageIcon className="h-5 w-5" />} value={`${stats.fotos}/${stats.hermandades}`} label="Fotos del libro" color="#f472b6" />
        </div>

        {/* Calendario oficial 2026: Miércoles de Pasión 02/09 → Domingo de Resurrección 13/09 */}
        {diasDesajustados.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-sky-500/30 bg-sky-500/10 p-4">
            <CalendarDays className="h-5 w-5 flex-shrink-0 text-sky-400" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-sky-300">
                {diasDesajustados.length} día(s) con fecha o sección distinta del calendario oficial
              </p>
              <p className="text-xs text-sky-200/70">
                Semana Santa de Jartiland 2026: Miércoles de Pasión el 2 de septiembre y Domingo de Resurrección el 13 de septiembre.
              </p>
            </div>
            <Button size="sm" onClick={aplicarFechasOficiales} disabled={aplicandoFechas}>
              {aplicandoFechas && <Loader2 className="h-4 w-4 animate-spin" />} Aplicar fechas oficiales
            </Button>
          </div>
        )}

        <LibroConfigCard config={programaConfig} cityMapUrl={cityMapUrl} onRefresh={fetchPageData} />

        {/* Solicitudes: recorridos y cuadros de horarios que mandan los hermanos mayores */}
        {(pendingRecorridos.length + pendingHorarios.length) > 0 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="flex items-center gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-400" />
              <div>
                <h2 className="text-lg font-bold text-amber-300">
                  {pendingRecorridos.length + pendingHorarios.length} solicitud{(pendingRecorridos.length + pendingHorarios.length) === 1 ? '' : 'es'} esperando revisión
                </h2>
                <p className="text-xs text-amber-200/70">
                  {pendingRecorridos.length} recorrido(s) y {pendingHorarios.length} cuadro(s) de horarios. Los recorridos se aprueban
                  eligiendo el color con el que saldrán en el libro; los horarios puedes retocarlos antes (y después) de aprobarlos.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {pendingRecorridos.map((item) => (
                <PendingRecorridoCard
                  key={`rec-${item.dia.id}-${item.hermandadId}`}
                  item={item}
                  allHermandades={allHermandades}
                  cityMapUrl={cityMapUrl}
                  carreraOficial={programaConfig?.carreraOficial}
                  catedralPunto={programaConfig?.catedralPunto}
                  onReviewed={fetchPageData}
                />
              ))}
              {pendingHorarios.map((item) => (
                <PendingHorarioCard
                  key={`hor-${item.dia.id}-${item.hermandadId}`}
                  item={item}
                  allHermandades={allHermandades}
                  onReviewed={fetchPageData}
                />
              ))}
            </div>
          </motion.div>
        )}

        <div className="space-y-8">
          {renderDaySection('Vísperas', 'Miércoles y jueves de pasión, viernes de dolores y sábado de pasión — sin Carrera Oficial: cada cofradía marca su estación de penitencia.', visperas, '#a78bfa')}
          {renderDaySection('Semana Santa', 'Del Domingo de Ramos al Domingo de Resurrección — con Carrera Oficial.', semanaSanta, '#fbbf24')}
        </div>

        {/* Zona peligrosa */}
        <Card className="border-destructive/30">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-destructive"><Eraser className="h-4 w-4" /> Zona peligrosa</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">Borrar todos los recorridos enviados</p>
                <p className="text-xs text-muted-foreground">
                  Vacía los {stats.recorridos} recorrido(s) de todas las hermandades (pendientes, aprobados y rechazados) para que se rehagan con el editor nuevo.
                </p>
              </div>
              <Button variant="destructive" onClick={() => setConfirmClear('recorridos')} disabled={stats.recorridos === 0}>
                <Trash2 className="h-4 w-4" /> Borrar recorridos
              </Button>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-3">
              <div>
                <p className="text-sm font-semibold">Borrar todos los cuadros de horarios</p>
                <p className="text-xs text-muted-foreground">
                  Vacía los {stats.itinerarios} cuadro(s) de horarios de todas las hermandades, incluidos los que hayas escrito tú a mano.
                </p>
              </div>
              <Button variant="destructive" onClick={() => setConfirmClear('horarios')} disabled={stats.itinerarios === 0}>
                <Trash2 className="h-4 w-4" /> Borrar horarios
              </Button>
            </div>
          </CardContent>
        </Card>

        {editingDay && <EditDayDialog dia={editingDay} onClose={() => setEditingDay(null)} onSaved={fetchPageData} />}

        {editingHermandad && (
          <Dialog open={!!editingHermandad} onOpenChange={(o) => !o && setEditingHermandad(null)}>
            <DialogContent className="flex max-h-[90dvh] flex-col sm:max-w-2xl">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  {allHermandades.find((h) => h.id === editingHermandad.hermandadId)?.nombre}
                </DialogTitle>
                <DialogDescription>Datos de la procesión para el {editingHermandad.dia.nombredia}.</DialogDescription>
              </DialogHeader>
              <EditDetailsForm
                dia={editingHermandad.dia}
                hermandadId={editingHermandad.hermandadId}
                hermandad={allHermandades.find((h) => h.id === editingHermandad.hermandadId)}
                allBandas={allBandas}
                programaConfig={programaConfig}
                onFormSuccess={handleFormSuccess}
                onRefresh={fetchPageData}
                onClose={() => setEditingHermandad(null)}
              />
            </DialogContent>
          </Dialog>
        )}

        <AlertDialog open={!!confirmClear} onOpenChange={(o) => !o && setConfirmClear(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmClear === 'horarios' ? '¿Borrar TODOS los cuadros de horarios?' : '¿Borrar TODOS los recorridos?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirmClear === 'horarios'
                  ? 'Se eliminarán las horas de todas las hermandades en todos los días, tanto las que enviaron ellas como las que escribiste tú. Tendrán que volver a mandarlas.'
                  : 'Se eliminarán los recorridos de todas las hermandades en todos los días. Las hermandades tendrán que volver a dibujarlos con el editor nuevo.'}
                {' '}Esto no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); handleClear(); }} disabled={clearing}>
                {clearing && <Loader2 className="h-4 w-4 animate-spin" />} Sí, borrarlos todos
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <DragOverlay>
        {activeId && activeHermandad ? (
          <div className="flex items-center gap-2 rounded-xl border border-primary bg-primary p-2 text-primary-foreground shadow-xl">
            {activeHermandad.escudoUrl && <img src={activeHermandad.escudoUrl} alt="" className="h-6 w-6 object-contain" />}
            <p className="font-semibold">{activeHermandad.nombre}</p>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
